import { type FieldRefusal, FORM_STARTED, FORM_VIEWED, type FormAfterSubmit, type FormChallenge, type FormField, FormRefusedError, type ServedForm } from 'ProdantixSdk/core/forms';
import { solve } from 'ProdantixSdk/web/forms/challenge';
import { FORM_STRINGS, type FormLanguage, type FormString } from 'ProdantixSdk/web/forms/strings';

/** What the renderer needs from the SDK: the published form, a submission,
 *  and the two analytics events. */
export interface FormsHost {
  capture(event: string, properties: Record<string, unknown>): void;
  form(formId: string): Promise<ServedForm>;
  submit(formId: string, answers: Record<string, unknown>, challenge: FormChallenge, solution: string): Promise<FormAfterSubmit>;
}

export interface RenderFormOptions {
  formId: string;
  host: FormsHost;
  language: FormLanguage;
  /** Whether the form carries the "Powered by Prodantix" line. */
  poweredBy?: boolean;
}

export interface RenderedForm {
  destroy(): void;
  ready: Promise<void>;
}

const CLASS = 'prodantix-form';

const STYLE = `.${CLASS}{font-family:var(--pdx-font);color:var(--pdx-text);background:var(--pdx-background);max-width:var(--pdx-width);box-sizing:border-box;padding:16px;border-radius:var(--pdx-radius)}
.${CLASS} *{box-sizing:border-box}
.${CLASS} .pdx-field{display:flex;flex-direction:column;gap:4px;margin:0 0 12px}
.${CLASS} .pdx-field[hidden]{display:none}
.${CLASS} label,.${CLASS} legend{font-weight:600;font-size:14px}
.${CLASS} input:not([type=checkbox]):not([type=radio]),.${CLASS} select,.${CLASS} textarea{font:inherit;color:var(--pdx-field-text);background:var(--pdx-field-background);border:1px solid currentColor;border-radius:var(--pdx-radius);padding:10px;min-height:44px;width:100%}
.${CLASS} .pdx-choice{display:flex;align-items:center;gap:8px;min-height:44px}
.${CLASS} .pdx-help,.${CLASS} .pdx-meta{font-size:12px;opacity:.85}
.${CLASS} .pdx-error{font-size:12px;font-weight:600}
.${CLASS} button{font:inherit;min-height:44px;padding:0 18px;border:0;border-radius:var(--pdx-radius);background:var(--pdx-button);color:var(--pdx-button-text);cursor:pointer}
.${CLASS} button[data-variant=back]{background:transparent;color:inherit;border:1px solid currentColor}
.${CLASS} .pdx-actions{display:flex;gap:8px;justify-content:flex-end}
.${CLASS} .pdx-hp{position:absolute;left:-10000px;width:1px;height:1px;overflow:hidden}
.${CLASS} :focus-visible{outline:2px solid var(--pdx-button);outline-offset:2px}`;

type Answers = Record<string, unknown>;

const isEmpty = (value: unknown): boolean => value === undefined || value === null || value === false || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0);

/** Whether a field's condition holds for the answers so far, as the edge
 *  decides it. */
export const isShown = (field: FormField, answers: Answers): boolean => {
  const condition = field.condition;
  if (!condition) {
    return true;
  }
  const answer = answers[condition.field];
  if (isEmpty(answer)) {
    return false;
  }
  if (condition.op === 'is_set') {
    return true;
  }
  if (condition.op === 'equals') {
    return typeof answer === 'string' && answer === condition.values[0];
  }
  const given = Array.isArray(answer) ? answer : [answer];
  return given.some(item => typeof item === 'string' && condition.values.includes(item));
};

const readCookie = (name: string): string => {
  const prefix = `${encodeURIComponent(name)}=`;
  const found = document.cookie.split('; ').find(part => part.startsWith(prefix));
  return found ? decodeURIComponent(found.slice(prefix.length)) : '';
};

const hiddenValue = (field: FormField): string => {
  const hidden = field.hidden;
  if (!hidden) {
    return '';
  }
  if (hidden.source === 'fixed') {
    return hidden.value;
  }
  if (hidden.source === 'query') {
    return new URLSearchParams(window.location.search).get(hidden.value) ?? '';
  }
  return readCookie(hidden.value);
};

const element = <K extends keyof HTMLElementTagNameMap>(tag: K, properties: Partial<HTMLElementTagNameMap[K]> = {}, children: (Node | string)[] = []): HTMLElementTagNameMap[K] => {
  const node = Object.assign(document.createElement(tag), properties);
  node.append(...children);
  return node;
};

interface FieldView {
  error: HTMLElement;
  field: FormField;
  read: () => unknown;
  root: HTMLElement;
  focus: () => void;
}

const fieldView = (field: FormField, id: string, onInput: () => void): FieldView => {
  const error = element('p', { className: 'pdx-error', id: `${id}-error` });
  const help = field.help ? element('p', { className: 'pdx-help', id: `${id}-help` }, [field.help]) : null;
  const describedBy = [help ? `${id}-help` : '', `${id}-error`].filter(Boolean).join(' ');
  const root = element('div', { className: 'pdx-field' });
  root.dataset.field = field.key;
  if (field.kind === 'rich_text') {
    root.append(element('p', {}, [field.label]));
    return { error, field, focus: () => undefined, read: () => undefined, root };
  }
  if (field.kind === 'hidden') {
    root.hidden = true;
    return { error, field, focus: () => undefined, read: () => hiddenValue(field), root };
  }
  if (field.kind === 'radio' || field.kind === 'checkboxes') {
    const fieldset = element('fieldset', {});
    fieldset.setAttribute('aria-describedby', describedBy);
    if (field.required) {
      fieldset.setAttribute('aria-required', 'true');
    }
    fieldset.append(element('legend', {}, [field.label]));
    const inputs = field.options
      .filter(option => !option.archived)
      .map((option, index) => {
        const input = element('input', { id: `${id}-${index}`, name: id, type: field.kind === 'radio' ? 'radio' : 'checkbox', value: option.value });
        input.addEventListener('change', onInput);
        fieldset.append(element('div', { className: 'pdx-choice' }, [input, element('label', { htmlFor: `${id}-${index}` }, [option.label])]));
        return input;
      });
    root.append(fieldset, ...(help ? [help] : []), error);
    const read = (): unknown => {
      const chosen = inputs.filter(input => input.checked).map(input => input.value);
      return field.kind === 'radio' ? (chosen[0] ?? '') : chosen;
    };
    return { error, field, focus: () => inputs[0]?.focus(), read, root };
  }
  const label = element('label', { htmlFor: id }, [field.label]);
  let control: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
  if (field.kind === 'textarea') {
    control = element('textarea', { id, placeholder: field.placeholder, rows: 4 });
  } else if (field.kind === 'dropdown') {
    control = element('select', { id }, [element('option', { value: '' }, ['']), ...field.options.filter(option => !option.archived).map(option => element('option', { value: option.value }, [option.label]))]);
  } else if (field.kind === 'checkbox') {
    control = element('input', { id, type: 'checkbox' });
  } else {
    const types: Partial<Record<FormField['kind'], string>> = { date: 'date', email: 'email', file: 'file', number: 'number', phone: 'tel' };
    control = element('input', { id, placeholder: field.placeholder, type: types[field.kind] ?? 'text' });
    if (field.kind === 'email') {
      control.autocomplete = 'email';
    }
  }
  control.setAttribute('aria-describedby', describedBy);
  if (field.required) {
    control.setAttribute('aria-required', 'true');
  }
  control.addEventListener('input', onInput);
  control.addEventListener('change', onInput);
  if (field.kind === 'checkbox') {
    root.append(element('div', { className: 'pdx-choice' }, [control, label]), ...(help ? [help] : []), error);
  } else {
    root.append(label, ...(help ? [help] : []), control, error);
  }
  const read = (): unknown => {
    if (control instanceof HTMLInputElement && control.type === 'checkbox') {
      return control.checked;
    }
    if (field.kind === 'number') {
      return control.value === '' ? '' : Number(control.value);
    }
    return control.value;
  };
  return { error, field, focus: () => control.focus(), read, root };
};

const applyStyle = (root: HTMLElement, form: ServedForm): void => {
  const style = form.style;
  const values: Record<string, string> = {
    '--pdx-background': style.background,
    '--pdx-button': style.button,
    '--pdx-button-text': style.buttonText,
    '--pdx-field-background': style.fieldBackground,
    '--pdx-field-text': style.fieldText,
    '--pdx-font': style.font,
    '--pdx-radius': `${style.radius}px`,
    '--pdx-text': style.text,
    '--pdx-width': `${style.width}px`,
  };
  for (const [name, value] of Object.entries(values)) {
    root.style.setProperty(name, value);
  }
};

const ensureStyleSheet = (): void => {
  if (document.getElementById('prodantix-form-style')) {
    return;
  }
  document.head.append(element('style', { id: 'prodantix-form-style', textContent: STYLE }));
};

/** Draws a published form into `target` and takes its submission. */
export const renderForm = (target: HTMLElement, options: RenderFormOptions): RenderedForm => {
  const { formId, host, language } = options;
  const words = FORM_STRINGS[language];
  const say = (key: FormString): string => words[key];
  const root = element('form', { className: CLASS, noValidate: true });
  root.dataset.prodantixForm = formId;
  const live = element('p', { className: 'pdx-meta' });
  live.setAttribute('aria-live', 'polite');
  live.setAttribute('role', 'status');
  let destroyed = false;

  const ready = host
    .form(formId)
    .then(form => {
      if (destroyed) {
        return;
      }
      if (!form.accepting) {
        root.replaceChildren(element('p', { className: 'pdx-meta' }, [say('closed')]));
        return;
      }
      ensureStyleSheet();
      applyStyle(root, form);
      const solution = form.challenge ? solve(form.challenge) : null;
      let started = false;
      const views: FieldView[][] = [];
      const answers = (): Answers => {
        const collected: Answers = {};
        for (const view of views.flat()) {
          if (!view.root.hidden || view.field.kind === 'hidden') {
            const value = view.read();
            if (value !== undefined) {
              collected[view.field.key] = value;
            }
          }
        }
        return collected;
      };
      const refresh = (): void => {
        const current = answers();
        for (const view of views.flat()) {
          if (view.field.kind !== 'hidden') {
            view.root.hidden = !isShown(view.field, current);
          }
        }
      };
      const onInput = (): void => {
        if (!started) {
          started = true;
          host.capture(FORM_STARTED, { $form_id: form.id, $form_version: form.version });
        }
        refresh();
      };
      const stepNodes = form.steps.map((step, stepIndex) => {
        const section = element('div', {});
        section.dataset.step = String(stepIndex);
        if (step.title) {
          section.append(element('p', { className: 'pdx-meta' }, [step.title]));
        }
        const stepViews = step.fields.map(field => fieldView(field, `pdx-${formId}-${field.key}`, onInput));
        views.push(stepViews);
        section.append(...stepViews.map(view => view.root));
        return section;
      });
      const honeypot = element('input', { autocomplete: 'off', name: form.honeypot, tabIndex: -1, type: 'text' });
      const trap = element('div', { className: 'pdx-hp' }, [honeypot]);
      trap.setAttribute('aria-hidden', 'true');
      const consents: { input: HTMLInputElement; key: string }[] = [];
      const consentBlock = element('div', {});
      if (form.consent.processing) {
        const input = element('input', { id: `pdx-${formId}-consent`, type: 'checkbox' });
        input.setAttribute('aria-required', 'true');
        consents.push({ input, key: '$consent_processing' });
        consentBlock.append(element('div', { className: 'pdx-choice' }, [input, element('label', { htmlFor: input.id }, [form.consent.processing])]));
      }
      for (const purpose of form.consent.purposes) {
        const input = element('input', { id: `pdx-${formId}-consent-${purpose.key}`, type: 'checkbox' });
        consents.push({ input, key: `$consent_${purpose.key}` });
        consentBlock.append(element('div', { className: 'pdx-choice' }, [input, element('label', { htmlFor: input.id }, [purpose.label])]));
        if (purpose.notice) {
          consentBlock.append(element('p', { className: 'pdx-help' }, [purpose.notice]));
        }
      }
      const progress = element('p', { className: 'pdx-meta' });
      const back = element('button', { type: 'button' }, [say('back')]);
      back.dataset.variant = 'back';
      const next = element('button', { type: 'button' }, [say('next')]);
      const submit = element('button', { type: 'submit' }, [say('submit')]);
      back.dataset.action = 'back';
      next.dataset.action = 'next';
      submit.dataset.action = 'submit';
      const actions = element('div', { className: 'pdx-actions' }, [back, next, submit]);
      let step = 0;
      const show = (): void => {
        stepNodes.forEach((node, index) => {
          node.hidden = index !== step;
        });
        const last = step === stepNodes.length - 1;
        back.hidden = step === 0;
        next.hidden = last;
        submit.hidden = !last;
        consentBlock.hidden = !last;
        progress.hidden = stepNodes.length < 2;
        progress.textContent = say('step')
          .replace('{step}', String(step + 1))
          .replace('{total}', String(stepNodes.length));
      };
      const clearErrors = (): void => {
        for (const view of views.flat()) {
          view.error.textContent = '';
          view.root.querySelector('[aria-invalid]')?.removeAttribute('aria-invalid');
        }
      };
      const flag = (view: FieldView, message: string): void => {
        view.error.textContent = message;
        view.root.querySelector('input,select,textarea,fieldset')?.setAttribute('aria-invalid', 'true');
      };
      const checkStep = (index: number): boolean => {
        clearErrors();
        const current = answers();
        const missing = (views[index] ?? []).filter(view => !view.root.hidden && view.field.required && isEmpty(current[view.field.key]));
        for (const view of missing) {
          flag(view, say('required'));
        }
        if (missing.length > 0) {
          live.textContent = say('required');
          missing[0]?.focus();
          return false;
        }
        return true;
      };
      back.addEventListener('click', () => {
        step = Math.max(0, step - 1);
        show();
      });
      next.addEventListener('click', () => {
        if (checkStep(step)) {
          step += 1;
          show();
        }
      });
      root.addEventListener('submit', event => {
        event.preventDefault();
        if (!checkStep(step)) {
          return;
        }
        const submitted: Answers = { ...answers(), [form.honeypot]: honeypot.value };
        for (const consent of consents) {
          submitted[consent.key] = consent.input.checked;
        }
        submit.disabled = true;
        submit.textContent = say('sending');
        const challenge = form.challenge;
        if (!challenge || !solution) {
          live.textContent = say('closed');
          return;
        }
        void solution
          .then(answer => host.submit(formId, submitted, challenge, answer))
          .then(after => {
            if (after.kind === 'redirect' && after.value.startsWith('https://')) {
              window.location.assign(after.value);
              return;
            }
            const thanks = element('p', {}, [after.kind === 'message' && after.value ? after.value : say('thanks')]);
            thanks.setAttribute('role', 'status');
            root.replaceChildren(thanks);
          })
          .catch((error: unknown) => {
            submit.disabled = false;
            submit.textContent = say('submit');
            const refused: FieldRefusal[] = error instanceof FormRefusedError ? error.fields : [];
            for (const refusal of refused) {
              const view = views.flat().find(candidate => candidate.field.key === refusal.field);
              if (view) {
                flag(view, refusal.rule === 'required' ? say('required') : say('invalid'));
              }
            }
            live.textContent = refused.length > 0 ? say('invalid') : say('failed');
          });
      });
      root.append(progress, ...stepNodes, trap, consentBlock, actions, live);
      if (form.poweredBy && options.poweredBy !== false) {
        root.append(element('p', { className: 'pdx-meta' }, [say('poweredBy')]));
      }
      show();
      refresh();
      host.capture(FORM_VIEWED, { $form_id: form.id, $form_version: form.version });
    })
    .catch(() => {
      if (!destroyed) {
        root.replaceChildren(element('p', { className: 'pdx-meta' }, [say('closed')]));
      }
    });
  target.append(root);
  return {
    destroy: () => {
      destroyed = true;
      root.remove();
    },
    ready,
  };
};
